import { supabase } from './supabase';
import type {
  Phase,
  Priority,
  Product,
  ProjectCategory,
  ProjectLifecycle,
  StageTemplate,
} from '../shared/types';

/** Normaliza relação "to-one" (objeto em runtime, array no cliente sem tipos). */
function rel<T>(v: unknown): T | null {
  if (Array.isArray(v)) return (v[0] as T) ?? null;
  return (v as T) ?? null;
}

/** Carrega o template (etapas + dependências + áreas) da organização. */
export async function loadTemplate(orgId: string): Promise<StageTemplate[]> {
  const [{ data: tpl }, { data: deps }, { data: areas }] = await Promise.all([
    supabase
      .from('stage_templates')
      .select('n, phase, name, default_days')
      .eq('org_id', orgId)
      .order('n'),
    supabase.from('stage_template_deps').select('n, depends_on_n').eq('org_id', orgId),
    supabase.from('stage_template_areas').select('n, areas(key)').eq('org_id', orgId),
  ]);

  const depMap = new Map<number, number[]>();
  for (const d of (deps as { n: number; depends_on_n: number }[]) ?? []) {
    const arr = depMap.get(d.n) ?? [];
    arr.push(d.depends_on_n);
    depMap.set(d.n, arr);
  }
  const areaMap = new Map<number, string[]>();
  for (const row of ((areas ?? []) as unknown[])) {
    const a = row as { n: number; areas: unknown };
    const key = rel<{ key: string }>(a.areas)?.key;
    if (!key) continue;
    const arr = areaMap.get(a.n) ?? [];
    arr.push(key);
    areaMap.set(a.n, arr);
  }

  return ((tpl as { n: number; phase: string; name: string; default_days: number }[]) ?? []).map(
    (t) => ({
      n: t.n,
      phase: t.phase as Phase,
      name: t.name,
      defaultDays: t.default_days,
      deps: depMap.get(t.n) ?? [],
      areaKeys: areaMap.get(t.n) ?? [],
    }),
  );
}

interface RawStage {
  product_id: string;
  n: number;
  days_planned: number;
  status: 'nao_iniciado' | 'em_andamento' | 'concluido';
  started_at: string | null;
  finished_at: string | null;
  comment: string | null;
}

/** Carrega todas as linhas de produto da org com suas 24 etapas. */
export async function loadProducts(orgId: string): Promise<Product[]> {
  const [{ data: prods }, { data: stages }] = await Promise.all([
    supabase
      .from('products')
      .select(
        'id, name, sap_code, opened_at, target_launch, priority, category, lifecycle, project_type, cancel_reason, photo_url, brand_id, brands(name)',
      )
      .eq('org_id', orgId)
      .order('name'),
    supabase
      .from('product_stages')
      .select('product_id, n, days_planned, status, started_at, finished_at, comment')
      .eq('org_id', orgId)
      .order('n'),
  ]);

  const byProduct = new Map<string, RawStage[]>();
  for (const s of (stages as RawStage[]) ?? []) {
    const arr = byProduct.get(s.product_id) ?? [];
    arr.push(s);
    byProduct.set(s.product_id, arr);
  }

  return (((prods ?? []) as unknown[]) as Record<string, unknown>[]).map((raw) => {
    const p = raw as {
      id: string;
      name: string;
      sap_code: string | null;
      opened_at: string | null;
      target_launch: string | null;
      priority: Priority | null;
      category: ProjectCategory | null;
      lifecycle: ProjectLifecycle | null;
      project_type: 'interno' | 'terceirizado' | null;
      cancel_reason: string | null;
      photo_url: string | null;
      brand_id: string | null;
      brands: unknown;
    };
    return {
    id: p.id,
    brandId: p.brand_id ?? undefined,
    brand: rel<{ name: string }>(p.brands)?.name ?? '—',
    name: p.name,
    sapCode: p.sap_code,
    openedAt: p.opened_at,
    targetLaunch: p.target_launch,
    priority: p.priority ?? 'media',
    category: p.category ?? 'inovacao',
    lifecycle: p.lifecycle ?? 'ativo',
    projectType: p.project_type ?? 'interno',
    cancelReason: p.cancel_reason,
    photoUrl: p.photo_url,
    stages: (byProduct.get(p.id) ?? []).map((s) => ({
      n: s.n,
      daysPlanned: s.days_planned,
      status: s.status,
      startedAt: s.started_at,
      finishedAt: s.finished_at,
      comment: s.comment,
    })),
    };
  });
}

/** Atualiza uma etapa (status/datas/dias/comentário). RLS decide se é permitido. */
export async function updateStage(
  productId: string,
  n: number,
  patch: Partial<{
    status: 'nao_iniciado' | 'em_andamento' | 'concluido';
    started_at: string | null;
    finished_at: string | null;
    days_planned: number;
    comment: string | null;
  }>,
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('product_stages')
    .update(patch)
    .eq('product_id', productId)
    .eq('n', n);
  return { error: error?.message };
}

/** Atualiza campos do projeto (nome, marca, prioridade, ciclo de vida, metas…). RLS decide. */
export async function updateProduct(
  productId: string,
  patch: Partial<{
    name: string;
    sap_code: string | null;
    brand_id: string;
    priority: Priority;
    category: ProjectCategory;
    opened_at: string | null;
    target_launch: string | null;
    lifecycle: ProjectLifecycle;
    cancel_reason: string | null;
    photo_url: string | null;
  }>,
): Promise<{ error?: string }> {
  const { error } = await supabase.from('products').update(patch).eq('id', productId);
  return { error: error?.message };
}

/** Define as áreas responsáveis por uma etapa (template da org). RLS: admin. */
export async function setStageAreas(
  orgId: string,
  n: number,
  areaIds: string[],
): Promise<{ error?: string }> {
  const del = await supabase
    .from('stage_template_areas')
    .delete()
    .eq('org_id', orgId)
    .eq('n', n);
  if (del.error) return { error: del.error.message };
  if (areaIds.length) {
    const rows = areaIds.map((id) => ({ org_id: orgId, n, area_id: id }));
    const ins = await supabase.from('stage_template_areas').insert(rows);
    if (ins.error) return { error: ins.error.message };
  }
  return {};
}

export interface BrandRow {
  id: string;
  name: string;
}

/** Marcas da organização (para o seletor ao criar um novo projeto). */
export async function loadBrands(orgId: string): Promise<BrandRow[]> {
  const { data } = await supabase
    .from('brands')
    .select('id, name')
    .eq('org_id', orgId)
    .order('name');
  return (data ?? []) as BrandRow[];
}

/** Cria uma nova marca. RLS: admin/gestor. */
export async function createBrand(
  orgId: string,
  name: string,
): Promise<{ error?: string; brand?: BrandRow }> {
  const { data, error } = await supabase
    .from('brands')
    .insert({ org_id: orgId, name })
    .select('id, name')
    .single();
  return { error: error?.message, brand: (data as BrandRow) ?? undefined };
}

/**
 * Cria um novo projeto (linha de produto). O trigger `on_product_created`
 * instancia automaticamente as 24 etapas a partir do template da org, e o
 * trigger da capa cria o product_card. RLS: admin/gestor. Devolve o id novo.
 */
export async function createProduct(params: {
  orgId: string;
  brandId: string;
  name: string;
  sapCode?: string | null;
  projectType?: 'interno' | 'terceirizado';
  priority?: Priority;
  category?: ProjectCategory;
  openedAt?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const { data, error } = await supabase
    .from('products')
    .insert({
      org_id: params.orgId,
      brand_id: params.brandId,
      name: params.name,
      sap_code: params.sapCode ?? null,
      project_type: params.projectType ?? 'interno',
      priority: params.priority ?? 'media',
      category: params.category ?? 'inovacao',
      opened_at: params.openedAt ?? null,
    })
    .select('id')
    .single();
  return { error: error?.message, id: (data as { id: string } | null)?.id };
}
